import { useEffect, useRef, useState } from 'react';
import { getDistrictById } from '~/services/districts';

const usePlaces = (
  districtId,
  getCurrentLocation,
  tripType,
  onPlaceChangeCallback,
  onCityChangeCallback
) => {
  const searchRef = useRef();
  const mapRef = useRef();
  const mapInstance = useRef();
  const markerRef = useRef();

  const [place, setPlace] = useState('');
  const [coordinates, setCoordinates] = useState();
  const [isMapLoading, setIsMapLoading] = useState(false);
  const [currentLocation, setCurrentLocation] = useState();

  useEffect(() => {
    if (!searchRef.current) return;

    if (tripType === 'TRIPS' && currentLocation) {
      searchRef.current.value = currentLocation.formatted_address;
      searchRef.current.placeholder = 'Sua localização atual';

      if (mapInstance.current) {
        if (currentLocation.geometry.viewport) {
          mapInstance.current.fitBounds(currentLocation.geometry.viewport);
        } else {
          mapInstance.current.setCenter(currentLocation.geometry.location);
          mapInstance.current.setZoom(23);
        }
      }

      if (markerRef.current) {
        markerRef.current.setPosition(currentLocation.geometry.location);
        markerRef.current.setVisible(true);
      }
    }

    if (tripType === 'DELIVERY') {
      searchRef.current.value = '';
      searchRef.current.placeholder = 'Endereço para retirada do pedido';

      if (mapInstance.current) {
        mapInstance.current.setCenter({
          lat: -9.8306521515871,
          lng: -51.07389452170478,
        });
        mapInstance.current.setZoom(4);
      }

      if (markerRef.current) {
        markerRef.current.setVisible(false);
      }
    }
  }, [tripType, currentLocation]);

  useEffect(() => {
    if (!searchRef.current || !districtId) return;

    const request = async () => {
      const response = await getDistrictById(districtId);
      const boundry = {
        latitude: Number(response?.data?.boundry?.latitude),
        longitude: Number(response?.data?.boundry?.longitude),
        radius: Number(response?.data?.boundry?.radius),
      };

      const bounds = {
        north: boundry.latitude + boundry.radius / 100,
        south: boundry.latitude - boundry.radius / 100,
        east: boundry.longitude + boundry.radius / 100,
        west: boundry.longitude - boundry.radius / 100,
      };

      const autocomplete = new window.google.maps.places.Autocomplete(
        searchRef.current,
        {
          fields: [
            'formatted_address',
            'geometry',
            'name',
            'address_components',
          ],
          componentRestrictions: { country: 'br' },
          ...(boundry.radius
            ? {
                bounds,
                strictBounds: true,
              }
            : {}),
        }
      );

      let map = null;
      let marker = null;

      if (mapRef.current) {
        map = new window.google.maps.Map(mapRef.current, {
          center: { lat: -9.8306521515871, lng: -51.07389452170478 },
          zoom: 4,
          mapTypeControl: false,
        });

        mapInstance.current = map;

        map.addListener('center_changed', () => {
          setIsMapLoading(true);
          setTimeout(() => {
            setIsMapLoading(false);
          }, 500);
        });

        marker = new window.google.maps.Marker({
          map,
          anchorPoint: new window.google.maps.Point(0, -29),
        });

        markerRef.current = marker;
      }

      if (navigator.geolocation && getCurrentLocation) {
        searchRef.current.placeholder = 'Buscando sua localização...';

        navigator.geolocation.getCurrentPosition(pos => {
          const geocoder = new window.google.maps.Geocoder();
          geocoder
            .geocode({
              location: {
                lat: pos.coords.latitude,
                lng: pos.coords.longitude,
              },
            })
            .then(geoResponse => {
              const address = geoResponse.results[0];
              if (!address.geometry?.location) {
                window.alert('Localização não encontrada.');
                return;
              }

              if (map) {
                if (address.geometry.viewport) {
                  map.fitBounds(address.geometry.viewport);
                } else {
                  map.setCenter(address.geometry.location);
                  map.setZoom(23);
                }
              }

              if (marker) {
                marker.setPosition(address.geometry.location);
                marker.setVisible(true);
              }

              const formattedAddress = address.formatted_address;
              const coords = {
                lat: address.geometry.location.lat(),
                long: address.geometry.location.lng(),
              };

              searchRef.current.value = formattedAddress;
              setPlace(formattedAddress);
              setCoordinates(coords);
              setCurrentLocation(address);

              if (onPlaceChangeCallback) {
                onPlaceChangeCallback({
                  name: formattedAddress,
                  latitude: coords.lat,
                  longitude: coords.long,
                  reference: 'Rua',
                });
              }

              if (onCityChangeCallback) {
                const cityComponent = address.address_components?.find(c =>
                  c.types.includes('administrative_area_level_2')
                );
                if (cityComponent) {
                  onCityChangeCallback(cityComponent.long_name);
                }
              }
            })
            .catch(e => window.alert(`Geocoder failed due to: ${e}`));
        });
      }

      autocomplete.addListener('place_changed', () => {
        const selectedPlace = autocomplete.getPlace();

        if (!selectedPlace.geometry?.location) {
          window.alert('Localização não encontrada.');
          return;
        }

        if (map) {
          if (selectedPlace.geometry.viewport) {
            map.fitBounds(selectedPlace.geometry.viewport);
          } else {
            map.setCenter(selectedPlace.geometry.location);
            map.setZoom(23);
          }
        }

        if (marker) {
          marker.setPosition(selectedPlace.geometry.location);
          marker.setVisible(true);
        }

        const formattedPlace = selectedPlace.formatted_address;
        const coords = {
          lat: selectedPlace.geometry.location.lat(),
          long: selectedPlace.geometry.location.lng(),
        };

        setPlace(formattedPlace);
        setCoordinates(coords);

        if (onPlaceChangeCallback) {
          onPlaceChangeCallback({
            name: formattedPlace,
            latitude: coords.lat,
            longitude: coords.long,
            reference: 'Rua',
          });
        }

        if (onCityChangeCallback) {
          const cityComponent = selectedPlace.address_components?.find(c =>
            c.types.includes('administrative_area_level_2')
          );
          if (cityComponent) {
            onCityChangeCallback(cityComponent.long_name);
          }
        }
      });
    };

    request();
  }, [districtId, getCurrentLocation]);

  return {
    place,
    searchRef,
    mapRef,
    coordinates,
    isMapLoading,
    currentLocation,
  };
};

export default usePlaces;
